import {MatchParams} from '@wandb/common/types/base';
import {useBooleanState} from '@wandb/common/util/hooks';
import {SetState} from '@wandb/common/util/types';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useSearchNavQueryQuery} from '../generated/graphql';
import {useCurrentRouteMatchParams} from '../routes/utils';
import {isInIframe} from '../setup';
import {SearchNavQueryData} from './Search';

type NavContextState = {
  params: MatchParams;
  hidden: boolean;
  shouldShowBreadcrumbs: boolean;
  searchLoading: boolean;
  searchData: SearchNavQueryData | undefined;
  searchActive: boolean;
};
type NavUpdaterContextState = {
  setHidden: SetState<boolean>;
  setShouldShowBreadcrumbs: SetState<boolean>;
  toggleSearch: () => void;
  activateSearch: () => void;
  deactivateSearch: () => void;
};

const NavContext = createContext<NavContextState>({
  params: {},
  hidden: false,
  shouldShowBreadcrumbs: true,
  searchLoading: false,
  searchData: undefined,
  searchActive: false,
});
const NavUpdaterContext = createContext<NavUpdaterContextState>({
  setHidden: () => {},
  setShouldShowBreadcrumbs: () => {},
  toggleSearch: () => {},
  activateSearch: () => {},
  deactivateSearch: () => {},
});

export function useNavContext(): NavContextState {
  return useContext(NavContext);
}
export function useNavUpdaterContext(): NavUpdaterContextState {
  return useContext(NavUpdaterContext);
}

type NavContextProviderProps = {};

const NavContextProvider: React.FC<NavContextProviderProps> = React.memo(
  ({children}) => {
    const [hidden, setHidden] = useState(false);
    const [shouldShowBreadcrumbs, setShouldShowBreadcrumbs] = useState(true);
    const {
      state: searchActive,
      setTrue: activateSearch,
      setFalse: deactivateSearch,
      toggle: toggleSearch,
    } = useBooleanState(false);

    const params = useCurrentRouteMatchParams();
    const {loading: searchLoading, data: searchNavData} =
      useSearchNavQueryQuery({
        variables: {
          entityName: params.entityName ?? '',
          projectName: params.projectName ?? '',
          runName: params.runName ?? '',
          sweepName: params.sweepName ?? '',
        },
        skip: hidden || isInIframe(),
      });
    const searchData = searchNavData as SearchNavQueryData | undefined;

    const value = useMemo(
      () => ({
        params,
        hidden,
        shouldShowBreadcrumbs,
        searchLoading,
        searchData,
        searchActive,
      }),
      [
        params,
        hidden,
        shouldShowBreadcrumbs,
        searchLoading,
        searchData,
        searchActive,
      ]
    );

    const updaterValue = useMemo(
      () => ({
        setHidden,
        setShouldShowBreadcrumbs,
        toggleSearch,
        activateSearch,
        deactivateSearch,
      }),
      [toggleSearch, activateSearch, deactivateSearch]
    );

    return (
      <NavContext.Provider value={value}>
        <NavUpdaterContext.Provider value={updaterValue}>
          {children}
        </NavUpdaterContext.Provider>
      </NavContext.Provider>
    );
  }
);

export default NavContextProvider;

export function useHideNavOnPage(): void {
  const {setHidden} = useNavUpdaterContext();
  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
